import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { isMobileOnly } from 'react-device-detect';
import { isEmpty } from 'lodash-es';
import styled, { css } from 'styled-components';
import {
  Heading4,
  Heading3,
  DoconCard,
  DoconTabs,
} from '@docon/docon-ui-library';
import {
  getClinics,
  getVcPrice,
  getDoctorDetails,
} from 'redux/reducer/doctor';
import { getNextAvailableSlot } from 'redux/reducer/shift';
import Layout, { MainBody } from 'components/Layout';
import HeaderBar from 'components/Layout/HeaderNavBar';
import DocItem from 'components/Doctors/DocItem';
import About from './About';
import Clinic from './Clinic';
import CustomHeader from './CustomHeader';
import { Loader } from 'shared';

const Card = styled(DoconCard)`
  display: block;
  padding: 0;

  & + & { margin-top: 10px; }

   .docItem {
    gap: 1.2rem;
    padding: 1.5rem 1.5rem 1rem;

    ${Heading3} {
      font-size: 36px;
      line-height: 40px;
    }
    ${Heading4} {
      font-size: 21px;
      line-height: 30px;
    } 
  }
`;

const Root = styled(Layout)`
  ${() => !isMobileOnly && css`
    ${MainBody} { padding-top: 0; }
  `}

  ${({ $isLoggedIn }) => !$isLoggedIn && css`
    ${MainBody} {
      max-width: 865px;
      padding-top: 16px;
      margin: auto;
    }
  `}

  ${() => isMobileOnly && css`

  .docItem {
    ${Heading3} {
      font-size: 26px;
      line-height: 32px;
    }

    ${Heading4} {
      font-size: 18px;
      line-height: 24px;
    }

    ${Card}:first-child {
      position: sticky;
      top: -170px;
    }
  `}
`;

const Tabs = [
  { title: 'CLINICS', value: 'clinics' },
  { title: 'ABOUT', value: 'about' },
];

function DoctorProfile() {
  const { accessToken } = useSelector(state => state.auth);
  const {
    isFetchingDoctor,
    clinicsByDocId,
    doctorsByShortUrl,
    vcPriceByDocId,
  } = useSelector(state => state.doctor);
  const {
    nextAvailableSlotByClinicId,
  } = useSelector(state => state.shift);
  const { shortUrl } = useParams<any>();
  const [selectedTab, setSelectedTab] = useState('clinics');
  const dispatch = useDispatch();

  const doctor = doctorsByShortUrl[shortUrl] || {};
  const doctorID = doctor?.doctorID || doctor?.docID;
  const clinics = clinicsByDocId[doctorID] || [];
  const vcPrice = vcPriceByDocId[doctorID] || {};

  // Note: Don't change these useEffect order
  useEffect(() => {
    if (!isEmpty(doctor)) {
      if (isEmpty(clinics))
        dispatch(getClinics(doctorID));
      if (isEmpty(vcPrice))
        dispatch(getVcPrice(doctorID));
    }
  }, [doctor]);

  useEffect(() => {
    if (!isEmpty(clinics)) {
      clinics.forEach(({ clinicID }) => {
        if (!nextAvailableSlotByClinicId[clinicID])
          dispatch(getNextAvailableSlot({
            doctorID,
            clinicID,
          }));
      });
    }
  }, [clinics]);

  useEffect(() => {
    if (!doctor.shortUrl) {
      dispatch(getDoctorDetails(shortUrl));
    }
  }, []);

  function handleTabChange(value) {
    setSelectedTab(value);
  }

  function renderCurrentTabContent() {
    if (isFetchingDoctor || doctor.shortUrl !== shortUrl) return null;

    switch (selectedTab) {
      case 'clinics':
        return (
          clinics.map((clinic: any) => (
            <Card key={clinic.clinicID}>
              <Clinic
                vcPrice={vcPrice}
                clinic={clinic}
                doctor={doctor}
                nextSlotDate={nextAvailableSlotByClinicId[clinic.clinicID]?.date}
              />
            </Card>
          ))
        );
      case 'about':
        return (
          <Card>
            <About doctors={doctor} />
          </Card>
        );
      default:
        return null;
    }
  }

  const Header = accessToken ? HeaderBar : CustomHeader;
  return (
    <Root
      $isLoggedIn={accessToken}
      hideFooter={isMobileOnly || !accessToken}
      header={isMobileOnly || !accessToken ? <Header title={'Doctor Profile'} /> : null}
    >
      {
        isMobileOnly || !accessToken ? null : (
          <Header title={'Doctor Profile'} />
        )
      }

      {
        isFetchingDoctor ? <Loader /> : null
      }

      <Card hidden={isFetchingDoctor}>
        <DocItem
          doctor={doctor}
          picSize={isMobileOnly ? '60px' : '110px'}
          className='docItem'
        />

        <DoconTabs
          tabWidth={''}
          verticalSpace={0}
          vertical={false}
          tabs={Tabs}
          selectedTab={selectedTab}
          onTabChange={handleTabChange}
          currentTabContent={null}
        />
      </Card>

      {renderCurrentTabContent()}

    </Root>
  );
}

export default DoctorProfile;